import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Copy, Search, BookOpen } from 'lucide-react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'https://api.azalega-academy.com';
const API_URL = API_BASE.endsWith('/api/v1') ? API_BASE : `${API_BASE}/api/v1`;

const EACopierPage = () => {
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState('newest');
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);

    // Predefined EA Copier tutorials with icons (fallback if no courses found)
    const tutorialDefaults = {
        'telegram-channel': { icon: '💬', iconBg: 'bg-blue-500', name: 'Cara Create Channel Telegram' },
        'telegram-bot': { icon: '🤖', iconBg: 'bg-cyan-500', name: 'Cara Create Bot Telegram' },
        'tradingview-alert': { icon: '📈', iconBg: 'bg-indigo-500', name: 'Cara Create Alert TradingView' },
        'webhook-setup': { icon: '🔗', iconBg: 'bg-purple-500', name: 'Cara Setup Webhook TradingView' }
    };

    useEffect(() => {
        fetchEACopierCourses();
    }, []);

    const fetchEACopierCourses = async () => {
        try {
            // Fetch all published courses with category 'sistem' and subcategory 'ea-copier'
            const response = await axios.get(`${API_URL}/courses`, {
                params: {
                    category: 'sistem',
                    videoSubcategory: 'ea-copier',
                    published: true
                }
            });

            const fetchedCourses = response.data.courses || response.data || [];

            // Map courses to tutorial format
            const mappedCourses = fetchedCourses.map(course => {
                // Try to match course tags/title to tutorial type
                const tutorial = Object.keys(tutorialDefaults).find(key =>
                    course.tags?.includes(key) ||
                    course.title?.toLowerCase().includes(key.replace('-', ' '))
                );

                const defaults = tutorial ? tutorialDefaults[tutorial] : {
                    icon: '📚',
                    iconBg: 'bg-teal-500',
                    name: course.title
                };

                return {
                    id: course._id,
                    name: course.title,
                    description: course.shortDescription || course.description,
                    icon: defaults.icon,
                    iconBg: defaults.iconBg,
                    lessons: course.totalLessons || course.modules?.reduce((acc, m) => acc + (m.lessons?.length || 0), 0) || 0,
                    duration: course.totalDuration ? `${Math.floor(course.totalDuration / 60)} min` : 'N/A',
                    route: `/courses/${course._id}`
                };
            });

            setCourses(mappedCourses);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching EA Copier courses:', error);
            setLoading(false);
        }
    };

    const filteredTutorials = courses.filter(tutorial =>
        tutorial.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        tutorial.description?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-black">
            {/* Header Section - WHITE BACKGROUND */}
            <div className="bg-[#ffffff] dark:bg-black border-b border-gray-200 dark:border-[#1e3a5f] py-8 px-6 transition-colors duration-300">
                <div className="max-w-7xl mx-auto">
                    <div className="flex items-center text-sm text-gray-500 dark:text-gray-400 mb-4">
                        <Link to="/student/dashboard" className="hover:text-gray-900 dark:hover:text-white transition-colors">Dashboard</Link>
                        <span className="mx-2">›</span>
                        <Link to="/my-courses" className="hover:text-gray-900 dark:hover:text-white transition-colors">Courses</Link>
                        <span className="mx-2">›</span>
                        <span className="text-gray-900 dark:text-white font-medium">EA Copier Setup</span>
                    </div>

                    <div className="flex items-center mb-6">
                        <div className="bg-purple-500 p-3 rounded-xl mr-4">
                            <Copy size={28} className="text-white" />
                        </div>
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-1">EA Copier Setup</h1>
                            <p className="text-gray-600 dark:text-gray-400">
                                Complete setup guides untuk integrate TradingView alerts dengan Telegram untuk auto trading signals.
                            </p>
                        </div>
                    </div>

                    {/* Stats Row */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="bg-gray-50 dark:!bg-black rounded-lg p-4 border border-gray-200 dark:border-gray-800 transition-colors duration-300">
                            <div className="flex items-center">
                                <div className="bg-purple-500 p-2 rounded-lg mr-3">
                                    <BookOpen size={18} className="text-white" />
                                </div>
                                <div>
                                    <div className="text-2xl font-bold text-gray-900 dark:text-white">{courses.length}</div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400">Setup Guides</div>
                                </div>
                            </div>
                        </div>
                        <div className="bg-gray-50 dark:!bg-black rounded-lg p-4 border border-gray-200 dark:border-gray-800 transition-colors duration-300">
                            <div className="flex items-center">
                                <div className="bg-blue-500 p-2 rounded-lg mr-3">
                                    <span className="text-white text-lg">📚</span>
                                </div>
                                <div>
                                    <div className="text-2xl font-bold text-gray-900 dark:text-white">
                                        {courses.reduce((acc, c) => acc + c.lessons, 0)}
                                    </div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400">Total Lessons</div>
                                </div>
                            </div>
                        </div>
                        <div className="bg-gray-50 dark:!bg-black rounded-lg p-4 border border-gray-200 dark:border-gray-800 transition-colors duration-300">
                            <div className="flex items-center">
                                <div className="bg-pink-500 p-2 rounded-lg mr-3">
                                    <span className="text-white text-lg">⏱️</span>
                                </div>
                                <div>
                                    <div className="text-2xl font-bold text-gray-900 dark:text-white">
                                        {courses.reduce((acc, c) => {
                                            const duration = parseInt(c.duration) || 0;
                                            return acc + duration;
                                        }, 0)}min
                                    </div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400">Total Content</div>
                                </div>
                            </div>
                        </div>
                        <div className="bg-gray-50 dark:!bg-black rounded-lg p-4 border border-gray-200 dark:border-gray-800 transition-colors duration-300">
                            <div className="flex items-center">
                                <div className="bg-yellow-500 p-2 rounded-lg mr-3">
                                    <span className="text-white text-lg">⭐</span>
                                </div>
                                <div>
                                    <div className="text-2xl font-bold text-gray-900 dark:text-white">4.9</div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400">Avg Rating</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Search & Filter Section */}
            <div className="bg-[#ffffff] dark:bg-black border-b border-gray-200 dark:border-gray-800 py-6 px-6 sticky top-0 z-10 shadow-sm transition-colors duration-300">
                <div className="max-w-7xl mx-auto">
                    <div className="flex flex-col md:flex-row gap-4">
                        <div className="flex-1 relative">
                            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                            <input
                                type="text"
                                placeholder="Search EA Copier tutorials..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-12 pr-4 py-3 border border-gray-300 dark:border-gray-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900 dark:text-white bg-white dark:bg-black placeholder-gray-500 transition-colors"
                            />
                        </div>
                        <div className="md:w-64">
                            <select
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900 dark:text-white bg-white dark:bg-black transition-colors"
                            >
                                <option value="newest">Newest First</option>
                                <option value="oldest">Oldest First</option>
                                <option value="name">Name (A-Z)</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>

            {/* Courses Grid */}
            <div className="max-w-7xl mx-auto px-6 py-8">
                {loading ? (
                    <div className="text-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto"></div>
                        <p className="text-gray-500 dark:text-gray-400 mt-4">Loading tutorials...</p>
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {filteredTutorials.map((tutorial) => (
                                <Link
                                    key={tutorial.id}
                                    to={tutorial.route}
                                    className="group bg-[#ffffff] dark:!bg-black rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden hover:border-purple-500 dark:hover:border-purple-500 hover:shadow-2xl hover:shadow-purple-500/20 transition-all duration-300"
                                >
                                    {/* Card Header with Icon */}
                                    <div className="p-6 border-b border-gray-100 dark:border-gray-800">
                                        <div className="flex items-center justify-between mb-4">
                                            <div className={`${tutorial.iconBg} w-16 h-16 rounded-xl flex items-center justify-center text-3xl shadow-lg group-hover:scale-110 transition-transform`}>
                                                {tutorial.icon}
                                            </div>
                                            <div className="text-right">
                                                <div className="text-sm text-gray-500 dark:text-gray-400">EA Copier</div>
                                            </div>
                                        </div>

                                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2 group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors">
                                            {tutorial.name}
                                        </h3>

                                        <p className="text-gray-600 dark:text-gray-400 text-sm line-clamp-2">
                                            {tutorial.description}
                                        </p>
                                    </div>

                                    {/* Card Footer */}
                                    <div className="p-6 bg-gray-50 dark:bg-black group-hover:bg-purple-50 dark:group-hover:bg-black transition-colors">
                                        <div className="flex items-center justify-between text-sm mb-4">
                                            <div className="flex items-center text-gray-600 dark:text-gray-400">
                                                <BookOpen size={16} className="mr-1" />
                                                <span>{tutorial.lessons} lessons</span>
                                            </div>
                                            <div className="flex items-center text-gray-600 dark:text-gray-400">
                                                <span className="mr-1">⏱️</span>
                                                <span>{tutorial.duration}</span>
                                            </div>
                                        </div>

                                        <div className="flex items-center justify-between">
                                            <span className="text-xs text-gray-500 dark:text-gray-500">Progress: 0%</span>
                                            <span className="text-purple-600 dark:text-purple-400 font-semibold group-hover:text-purple-700 dark:group-hover:text-purple-300 text-sm">
                                                Start Learning →
                                            </span>
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>

                        {/* No Results */}
                        {!loading && filteredTutorials.length === 0 && (
                            <div className="text-center py-12">
                                <div className="text-gray-400 mb-2">
                                    <Search size={48} className="mx-auto" />
                                </div>
                                <p className="text-gray-600 dark:text-gray-400 text-lg">
                                    {searchQuery
                                        ? `No tutorials found matching "${searchQuery}"`
                                        : 'No EA Copier tutorials available yet. Instructor needs to create courses with category "ea-copier".'}
                                </p>
                            </div>
                        )}

                        {/* Info Box */}
                        {courses.length > 0 && (
                            <div className="mt-12 bg-purple-50 dark:bg-purple-900/10 border border-purple-200 dark:border-purple-900/30 rounded-xl p-6">
                                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">🚀 Setup Workflow</h3>
                                <div className="text-gray-700 dark:text-gray-300 space-y-2">
                                    <p className="flex items-start">
                                        <span className="text-purple-600 dark:text-purple-400 mr-2 font-bold">1.</span>
                                        <strong>Setup Telegram Channel</strong> - Create channel untuk terima signals
                                    </p>
                                    <p className="flex items-start">
                                        <span className="text-purple-600 dark:text-purple-400 mr-2 font-bold">2.</span>
                                        <strong>Create Telegram Bot</strong> - Bot untuk forward messages
                                    </p>
                                    <p className="flex items-start">
                                        <span className="text-purple-600 dark:text-purple-400 mr-2 font-bold">3.</span>
                                        <strong>Setup TradingView Alerts</strong> - Configure indicator alerts
                                    </p>
                                    <p className="flex items-start">
                                        <span className="text-purple-600 dark:text-purple-400 mr-2 font-bold">4.</span>
                                        <strong>Connect Webhook</strong> - Link TradingView ↔ Telegram
                                    </p>
                                    <p className="mt-4 text-sm text-gray-600 dark:text-gray-400 italic">
                                        💡 Follow semua steps untuk fully automated trading signals system!
                                    </p>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default EACopierPage;
